import React from 'react';
import { Header, Icon, Message, Button, Grid, Form, Segment, Label } from 'semantic-ui-react';
import { formatCpfProgressive, cleanCpf, resolveCompleteCpfFromPartnerSource } from '../../services/utils/stringUtils';
import type { ContractRecebedorFormItem } from '../../types/contractForm';
import type { ClientResponse, ClientPartnerResponse } from '../../types/dtos';

export interface ContractFormRecebedoresTabProps {
  recebedores: ContractRecebedorFormItem[];
  setRecebedores: React.Dispatch<React.SetStateAction<ContractRecebedorFormItem[]>>;
  recebedorManual: { nome: string; qualificacao: string; cpf: string; rg: string };
  setRecebedorManual: React.Dispatch<
    React.SetStateAction<{ nome: string; qualificacao: string; cpf: string; rg: string }>
  >;
  selectedClient: ClientResponse | null | undefined;
  clientPartners: ClientPartnerResponse[];
}

export const ContractFormRecebedoresTab: React.FC<ContractFormRecebedoresTabProps> = ({
  recebedores,
  setRecebedores,
  recebedorManual,
  setRecebedorManual,
  selectedClient,
  clientPartners,
}) => {
  const addPartnerAsRecebedor = (partner: ClientPartnerResponse) => {
    // Não usar partner.id como id do ContractAuthorizedReceiver (mesmo problema do QSA no PATCH).
    if (recebedores.some((rec) => rec.id === `partner-${partner.id}` || rec.id === partner.id)) return;
    const completeCpf = resolveCompleteCpfFromPartnerSource(partner.cpfCnpj);
    setRecebedores((prev) => [
      ...prev,
      {
        id: `partner-${partner.id}`,
        nome: partner.nome,
        qualificacao: partner.qualificacao,
        cpf: completeCpf,
        rg: partner.rg,
        manual: false,
      },
    ]);
  };

  const addManualRecebedor = () => {
    if (!recebedorManual.nome.trim() || !recebedorManual.cpf.trim()) return;
    const novoRecebedor: ContractRecebedorFormItem = {
      id: `manual-${Date.now()}`,
      nome: recebedorManual.nome.trim(),
      qualificacao: recebedorManual.qualificacao.trim() || 'Recebedor',
      cpf: recebedorManual.cpf.trim(),
      rg: recebedorManual.rg.trim() || undefined,
      manual: true,
    };
    setRecebedores((prev) => [...prev, novoRecebedor]);
    setRecebedorManual({ nome: '', qualificacao: '', cpf: '', rg: '' });
  };

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="user plus" />
        Recebedores Autorizados
      </Header>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Selecione ou cadastre manualmente as pessoas autorizadas a receber comunicações sobre o contrato.
      </p>

      {recebedores.length > 0 && (
        <Segment style={{ marginBottom: '20px' }}>
          <Header as="h4" style={{ color: '#2c3e50' }}>
            <Icon name="user plus" />
            Recebedores Selecionados ({recebedores.length})
          </Header>
          {recebedores.map((rec, index) => (
            <div
              key={rec.id}
              style={{
                padding: '15px',
                borderBottom: index < recebedores.length - 1 ? '1px solid #eee' : 'none',
                backgroundColor: '#f8f9fa',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div>
                    <strong>{rec.nome}</strong>
                    <br />
                    <small style={{ color: '#666' }}>{rec.qualificacao || 'Sem qualificação'}</small>
                  </div>
                  {rec.cpf && (
                    <div style={{ marginTop: '2px', fontSize: '11px', color: '#666' }}>
                      CPF: {rec.cpf}
                      {rec.rg && ` | RG: ${rec.rg}`}
                    </div>
                  )}
                  {rec.manual ? (
                    <Label size="mini" color="orange" style={{ marginLeft: '8px', marginTop: '2px' }}>
                      Manual
                    </Label>
                  ) : (
                    <Label size="mini" color="blue" style={{ marginLeft: '8px', marginTop: '2px' }}>
                      QSA/Partner
                    </Label>
                  )}
                </div>
                <Button
                  size="small"
                  color="red"
                  icon="trash"
                  onClick={() => setRecebedores((prev) => prev.filter((r) => r.id !== rec.id))}
                />
              </div>

              <Form.Field style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>CPF (obrigatório)</label>
                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={formatCpfProgressive(rec.cpf || '')}
                  onChange={(e) => {
                    const cleanedValue = cleanCpf(e.target.value);
                    setRecebedores((prev) =>
                      prev.map((r) => (r.id === rec.id ? { ...r, cpf: cleanedValue } : r))
                    );
                  }}
                  maxLength={14}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    borderColor: !rec.cpf ? '#ff6b6b' : '#ccc',
                  }}
                />
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  Aceita CPF com ou sem formatação (11 dígitos)
                </div>
                {!rec.cpf && (
                  <small style={{ color: '#ff6b6b', fontSize: '11px' }}>CPF obrigatório para este recebedor</small>
                )}
              </Form.Field>
            </div>
          ))}
        </Segment>
      )}

      {recebedores.length === 0 && (
        <Message warning style={{ marginBottom: '20px' }}>
          <Message.Header>
            <Icon name="warning sign" />
            Atenção
          </Message.Header>
          <p>
            Você precisa adicionar pelo menos um <strong>Recebedor Autorizado</strong>. Selecione os QSA/Partners
            disponíveis abaixo ou cadastre manualmente.
          </p>
        </Message>
      )}

      <Segment>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
          <Icon name="search" />
          Adicionar Recebedor
        </Header>

        {selectedClient && clientPartners.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              <Icon name="users" />
              QSA/Partners disponíveis:
            </label>
            <div
              style={{
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginBottom: '15px',
              }}
            >
              {clientPartners
                .filter(
                  (partner) =>
                    !recebedores.some(
                      (rec) => rec.id === `partner-${partner.id}` || rec.id === partner.id
                    )
                )
                .map((partner) => (
                  <div
                    key={partner.id}
                    style={{
                      padding: '8px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => addPartnerAsRecebedor(partner)}
                  >
                    <div>
                      <strong>{partner.nome}</strong>
                      <br />
                      <small style={{ color: '#666' }}>{partner.qualificacao || 'Sem qualificação'}</small>
                    </div>
                    <Icon name="plus" color="green" />
                  </div>
                ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            <Icon name="edit" />
            Ou cadastre manualmente:
          </label>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={6}>
                <Form.Field required>
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    placeholder="Digite o nome completo"
                    value={recebedorManual.nome}
                    onChange={(e) => setRecebedorManual((prev) => ({ ...prev, nome: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={5}>
                <Form.Field required>
                  <label>CPF</label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    value={formatCpfProgressive(recebedorManual.cpf)}
                    onChange={(e) => {
                      const cleanedValue = cleanCpf(e.target.value);
                      setRecebedorManual((prev) => ({ ...prev, cpf: cleanedValue }));
                    }}
                    maxLength={14}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      borderColor: '#ccc',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 11 dígitos</div>
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={5}>
                <Form.Field>
                  <label>RG (opcional)</label>
                  <input
                    type="text"
                    placeholder="0000000000"
                    value={recebedorManual.rg}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 10);
                      setRecebedorManual((prev) => ({ ...prev, rg: value }));
                    }}
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 10 dígitos (opcional)</div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <Form.Field>
                  <label>Qualificação</label>
                  <input
                    type="text"
                    placeholder="Ex: Gerente, Diretor..."
                    value={recebedorManual.qualificacao}
                    onChange={(e) => setRecebedorManual((prev) => ({ ...prev, qualificacao: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <Button
                  color="blue"
                  onClick={addManualRecebedor}
                  disabled={!recebedorManual.nome.trim() || !recebedorManual.cpf.trim()}
                >
                  <Icon name="plus" />
                  Adicionar Recebedor Manual
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Segment>

      {!selectedClient && (
        <Message warning style={{ marginTop: '15px' }}>
          <Icon name="warning sign" />
          Selecione um cliente primeiro para carregar os QSA/Partners disponíveis.
        </Message>
      )}

      {selectedClient && clientPartners.length === 0 && (
        <Message info style={{ marginTop: '15px' }}>
          <Icon name="info circle" />
          Nenhum QSA/Partner encontrado para o cliente selecionado. Você pode cadastrar recebedores manualmente.
        </Message>
      )}
    </div>
  );
};
